<template>
  <div>
    <div class="header-container">
      <div class="header-section">
        <i class="icon-style" style="font-size: 14px"></i>
        <h6 class="mb-0 collection-name">Knee Sleeve Pre-Orders</h6>
      </div>
      <div class="header-section ml-auto search-container">
        <input
          type="text"
          class="form-control search-input"
          v-model="searchText"
          placeholder="Order ID"
          @keyup.enter="searchById"
          @sort-change="sortTable"
        />
        <i class="fa fa-search search-icon" @click="searchById"></i>
      </div>

      <button @click="reset" class="btn reset-btn">
        <i class="ri-refresh-line"></i>
      </button>
    </div>
    <div v-if="preorders.length">
      <el-table
        ref="table"
        :data="preorders"
        class="table background-container"
        style="width: 100%"
        @sort-change="sortTable"
        @row-click="handleRowClick"
        :default-sort="defaultSort"
        v-loading="loading"
      >
        <el-table-column
          v-for="column in tableColumns"
          :key="column.property"
          :label="column.label"
          show-overflow-tooltip
          :prop="column.property"
          :sortable="column.property == 'orderDate' ? 'custom' : false"
        >
          <template #default="scope">
            <div v-if="column.property === 'status'">
              <el-tag :type="getTagColorBasedOnEvent(formatPreorderData(scope.row, column))">{{ formatPreorderData(scope.row, column) }}</el-tag>
            </div>
            <div v-else>
              {{ formatPreorderData(scope.row, column) }}
            </div>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination">
        <div class="load-more-container">
          <p class="all-loaded-text">
            Total {{ searchCount }} <span class="vertical-divider">|</span>
          </p>
        </div>
        <div class="load-more-container" v-if="!allDataLoaded">
          <p
            @click="fetchNextPage('kneeSleevePreorders', sortQuery)"
            class="load-more-text"
          >
            Load more
          </p>
        </div>
        <div class="load-more-container" v-else>
          <p class="all-loaded-text">All documents loaded</p>
        </div>
      </div>
    </div>

    <div v-else class="mt-4 ml-2 mr-2 mb-2">
      <el-skeleton :rows="5" animated />
    </div>
  </div>
</template>

<script>
import {
  onMounted,
  ref
} from 'vue'
import {
  useRoute,
  useRouter
} from 'vue-router'
import getCollectionDocumentsCount from '../../../services/getCollectionDocumentsCount'
import getAllDocumentsWithPagination from '../../../services/getAllDocumentsWithPagination'

export default {
  name: 'PreordersTable',
  setup (props, context) {
    const route = useRoute()
    const tableColumns = ref([]) // This will store table column definitions
    const organizationOrderProperty = 'isOrganizationOrder'
    const searchText = ref('')
    const router = useRouter()
    const collectionId = ref('kneeSleevePreorders')
    const { count, getDocumentsCount } = getCollectionDocumentsCount(collectionId.value)
    const { documents: preorders, pageSize, loading, fetchDocuments, fetchNextPage, getDocumentSnapshot, allDataLoaded } = getAllDocumentsWithPagination()
    const searchCount = ref(0) // Initialize search count
    const defaultSort = ref({ prop: 'orderDate', order: 'descending' })
    const sortQuery = ref({ field: 'orderDate', direction: 'desc' })

    onMounted(async () => {
      const device = route.path.split('/').pop()
      await getDocumentsCount()
      searchCount.value = count.value
      await fetchDocuments(collectionId.value, sortQuery.value)
      getTableColumns()
    })

    const formatPreorderData = (preorder, column) => {
      if (column.property === 'orderDate') {
        return preorder.orderDate.toDate().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      }
      if (column.label === 'Name') {
        return `${preorder.firstName} ${preorder.lastName}`
      }
      if (column.property === organizationOrderProperty) {       
        return (preorder[organizationOrderProperty] === true || preorder[organizationOrderProperty] === 'true') ? 'Yes' : 'No'
      }

      if (column.property === 'status') {
        return preorder.timeline ? preorder.timeline[preorder.timeline.length - 1].event : preorder.status
      }

      return preorder[column.property]
    }

    const getTableColumns = () => {
      tableColumns.value = [{
        label: 'Order Id',
        property: 'id'
      },
      {
        label: 'Order Date',
        property: 'orderDate'
      },
      {
        label: 'Name'
      },
      {
        label: 'Quantity',
        property: 'quantity'
      },
      {
        label: 'Total',
        property: 'total'
      },
      {
        label: 'Organization',
        property: 'isOrganizationOrder'
      },
      {
        label: 'Status',
        property: 'status'
      }
      ]
    }

    const searchById = async () => {
      if (searchText.value.length) {
        await getDocumentSnapshot(collectionId.value, searchText.value)
        if (preorders.value.length) {
          searchCount.value = 1
        } else {
          searchCount.value = 0
        }
      } else {
        await fetchDocuments(collectionId.value)
        searchCount.value = count.value
      }
    }

    const reset = () => {
      searchText.value = ''
      fetchDocuments(collectionId.value, sortQuery.value)
    }

    const handleRowClick = (row, column, event) => {
      router.push({
        name: 'admin.preorderDetail',
        params: { id: row.id }
      })
    }

    const sortTable = (column, prop, order) => {
      const sortDirec = column.order === 'ascending' ? 'asc' : 'desc'
      defaultSort.value = { prop: column.column.rawColumnKey, order: column.order }
      sortQuery.value = { field: column.column.rawColumnKey, direction: sortDirec }
      fetchDocuments(collectionId.value, sortQuery.value)
    }
    const getTagColorBasedOnEvent = (event) => {
      switch (event) {
        case 'Payment received':
          return 'warning'
        case 'Preparing Order':
          return 'danger'
        case 'Ready for Dispatch':
          return 'info'
        case 'Order Shipped':
          return 'primary'
        case 'Order Delivered':
          return 'success'
        default:
          return 'info'
      }
    }

    return {
      tableColumns,
      searchText,
      searchById,
      reset,
      pageSize,
      handleRowClick,
      loading,
      preorders,
      formatPreorderData,
      searchCount,
      allDataLoaded,
      fetchNextPage,
      defaultSort,
      sortTable,
      getTagColorBasedOnEvent
    }
  }
}
</script>

<style scoped>
.header-container {
  display: flex;
  align-items: center;
  justify-content: start;
  width: 100%;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--border-color);
  padding: 15px 20px;
  background-color: var(--light-grey);
}

.header-section {
  display: flex;
  align-items: center;
}

.search-container {
  margin-left: auto;
  position: relative;
}

.search-input {
  width: auto;
  padding-right: 30px;
  height: 35px;
}

.search-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
}

.reset-btn {
  background-color: #e1e5e8;
  padding-left: 18px;
  margin-left: 10px;
}

.pagination {
  display: flex;
  justify-content: right;
  margin-right: 20px;
}

.load-more-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.load-more-text {
  cursor: pointer;
  color: #007bff;
  font-weight: bold;
  margin-top: 15px;
  padding-right: 10px;
}

.all-loaded-text {
  color: var(--muted-text-color) !important;
  margin-top: 15px;
  padding-right: 10px;
}

.vertical-divider {
  margin-left: 5px;
  height: auto;
  display: inline-block;
}
</style>

<style>
.el-loading-spinner .path {
  stroke: var(
    --accent-color
  ) !important; /* Replace #yourColor with your desired hex color */
}

.el-table .cell.el-tooltip {
  padding-left: 0 !important;
}
</style>
